import { useGlobalState } from '@opsopti/shared'
import { useLocation } from 'wouter'

import { NotFoundScreen } from '@/screens/404Screen'
import { AssetSentryAssetFormScreen } from '@/screens/AssetSentryAssetFormScreen'
import { AssetSentryGroupFormScreen } from '@/screens/AssetSentryGroupFormScreen'
import { AssetSentryTargetFormScreen } from '@/screens/AssetSentryTargetFormScreen'
import { AssetSentryTrackerFormScreen } from '@/screens/AssetSentryTrackerFormScreen'
import { ForgotPasswordScreen } from '@/screens/ForgotPasswordScreen'
import { LoginScreen } from '@/screens/LoginScreen'
import { PrivacyPolicyScreen } from '@/screens/PrivacyPolicyScreen'
import { ResetPasswordScreen } from '@/screens/ResetPasswordScreen'
import {
  AssetSentryAssetsScreen,
  AssetSentryAssignmentsScreen,
  AssetSentryGroupsScreen,
  AssetSentryTargetsScreen,
  AssetSentryTrackersScreen,
  UsersScreen,
} from '@/screens/TableScreen'
import { TermsOfServiceScreen } from '@/screens/TermsOfServiceScreen'
import { UserFormScreen } from '@/screens/UserFormScreen'
import type { RouteDef, RouteRedirectResponse, RouteRenderConditionResponse } from '@/utils/navigation'
import { getRoutePath } from '@/utils/navigation'

export const routes = {
  home: {
    path: '/',
    renderConditionFn: homeRenderCondition,
    routes: {
      auth: {
        path: '/auth',
        renderConditionFn: authRenderCondition,
        redirectFn: authRedirect,
        routes: {
          login: { path: '/login', component: LoginScreen },
          forgotPassword: { path: '/forgot-password', component: ForgotPasswordScreen },
          resetPassword: { path: '/reset-password/:token', component: ResetPasswordScreen },
        },
      },
      dashboard: {
        path: '/dashboard',
        renderConditionFn: dashboardRenderCondition,
        component: NotFoundScreen,
        routes: {
          users: { path: '/users', component: UsersScreen },
          userNew: { path: '/users/new', component: UserFormScreen },
          userEdit: { path: '/users/:id/edit', component: UserFormScreen },
          assetSentry: {
            path: '/asset-sentry',
            redirectFn: assetSentryRedirect,
            routes: {
              assetSentryAssets: { path: '/assets', component: AssetSentryAssetsScreen },
              assetSentryAssetNew: { path: '/assets/new', component: AssetSentryAssetFormScreen },
              assetSentryAssetEdit: { path: '/assets/:id/edit', component: AssetSentryAssetFormScreen },

              assetSentryGroups: { path: '/groups', component: AssetSentryGroupsScreen },
              assetSentryGroupNew: { path: '/groups/new', component: AssetSentryGroupFormScreen },
              assetSentryGroupEdit: { path: '/groups/:id/edit', component: AssetSentryGroupFormScreen },

              assetSentryTrackers: { path: '/trackers', component: AssetSentryTrackersScreen },
              assetSentryTrackerNew: { path: '/trackers/new', component: AssetSentryTrackerFormScreen },
              assetSentryTrackerEdit: { path: '/trackers/:id/edit', component: AssetSentryTrackerFormScreen },

              assetSentryTargets: { path: '/targets', component: AssetSentryTargetsScreen },
              assetSentryTargetNew: { path: '/targets/new', component: AssetSentryTargetFormScreen },
              assetSentryTargetEdit: { path: '/targets/:id/edit', component: AssetSentryTargetFormScreen },

              assetSentryAssignments: { path: '/assignments', component: AssetSentryAssignmentsScreen },
              assetSentryAssignmentNew: { path: '/assignments/new', component: NotFoundScreen },
              assetSentryAssignmentEdit: { path: '/assignments/:id/edit', component: NotFoundScreen },
            },
          },
        },
      },
      privacyPolicy: { path: '/privacy-policy', component: PrivacyPolicyScreen },
      termsOfService: { path: '/terms-of-service', component: TermsOfServiceScreen },
    },
  },
} as const satisfies RouteDef['routes']

export type RouteParams = {
  home: undefined
  privacyPolicy: undefined
  termsOfService: undefined

  auth: undefined
  login: undefined
  resetPassword: { token: string }
  forgotPassword: undefined

  dashboard: undefined
  assetSentry: undefined

  users: undefined
  userNew: undefined
  userEdit: { id: string }

  assetSentryAssets: undefined
  assetSentryAssetNew: undefined
  assetSentryAssetEdit: { id: string }

  assetSentryGroups: undefined
  assetSentryGroupNew: undefined
  assetSentryGroupEdit: { id: string }

  assetSentryTrackers: undefined
  assetSentryTrackerNew: undefined
  assetSentryTrackerEdit: { id: string }

  assetSentryTargets: undefined
  assetSentryTargetNew: undefined
  assetSentryTargetEdit: { id: string }

  assetSentryAssignments: undefined
  assetSentryAssignmentNew: undefined
  assetSentryAssignmentEdit: { id: string }
}

function homeRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const [pathname] = useLocation()
  const { isAuthenticated } = authenticationStore

  const allowedRoutes: string[] = [getRoutePath('privacyPolicy'), getRoutePath('termsOfService')]

  if (allowedRoutes.includes(pathname)) {
    return true
  }

  if (isAuthenticated) {
    return { redirectTo: getRoutePath('assetSentry') }
  } else {
    return { redirectTo: getRoutePath('auth') }
  }
}

function authRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated } = authenticationStore

  return !isAuthenticated || { redirectTo: getRoutePath('assetSentry') }
}

function dashboardRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated } = authenticationStore

  return isAuthenticated || { redirectTo: getRoutePath('auth') }
}

function authRedirect(): RouteRedirectResponse {
  return { redirectTo: getRoutePath('login') }
}

function assetSentryRedirect(): RouteRedirectResponse {
  return { redirectTo: getRoutePath('users') }
}
